import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { boAPI } from '../../lib/api';
import { toast } from 'sonner';
import { BOLayout } from './BOLayout';
import { ArrowLeft, Phone, Mail, MapPin, Calendar, Users, Link2, CreditCard, Award, User, Pencil } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useBOAuth } from '../../context/BOAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const InfoRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="h-4 w-4 text-neutral-400 mt-0.5 flex-shrink-0" />
      <div><p className="text-xs text-neutral-500">{label}</p><p className="font-medium text-neutral-800">{value}</p></div>
    </div>
  );
};

const ProfileAvatar = ({ marketer, size = 'lg' }) => {
  const [err, setErr] = useState(false);
  const url = marketer?.profile_image_url
    ? (marketer.profile_image_url.startsWith('/api') ? `${BACKEND_URL}${marketer.profile_image_url}` : marketer.profile_image_url)
    : null;
  const sz = size === 'lg' ? 'w-20 h-20' : 'w-10 h-10';
  if (!url || err) {
    return (
      <div className={`${sz} rounded-full bg-primary-200 flex items-center justify-center flex-shrink-0`}>
        <span className="font-bold text-primary-800 text-xl">{marketer?.full_name?.charAt(0).toUpperCase() || '?'}</span>
      </div>
    );
  }
  return <img src={url} alt="" className={`${sz} rounded-full object-cover flex-shrink-0 border-2 border-primary-200`} onError={() => setErr(true)} />;
};

const BORealtorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = useBOAuth();
  const isOwner = role === 'owner';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const loadDetail = async () => {
    try { const res = await boAPI.getRealtorDetail(id); setData(res.data); }
    catch (e) { toast.error('Failed to load realtor details'); navigate('/backoffice/realtors'); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadDetail(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const openEdit = () => {
    const m = data?.marketer || {};
    setEditForm({
      full_name: m.full_name || '',
      whatsapp: m.whatsapp || '',
      email: m.email || '',
      location: m.location || '',
      bank_name: m.bank_name || '',
      bank_account_number: m.bank_account_number || '',
      bank_account_name: m.bank_account_name || '',
      notes: m.notes || '',
    });
    setEditOpen(true);
  };
  const submitEdit = async () => {
    setSaving(true);
    try {
      // Strip empty strings so backend Pydantic validators don't reject empty EmailStr
      const payload = Object.fromEntries(Object.entries(editForm).filter(([, v]) => v !== ''));
      await boAPI.updateRealtor(id, payload);
      toast.success('Realtor updated');
      setEditOpen(false);
      loadDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  if (loading) return <BOLayout><div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600" /></div></BOLayout>;
  if (!data) return null;

  const { marketer, upline, downlines } = data;

  return (
    <BOLayout>
      <div className="space-y-6" data-testid="bo-realtor-detail">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/backoffice/realtors')} className="p-2 rounded-lg hover:bg-neutral-100" data-testid="back-to-realtors"><ArrowLeft className="h-5 w-5" /></button>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-neutral-900">Realtor Details</h1>
          </div>
          {isOwner && (
            <Button onClick={openEdit} variant="outline" className="rounded-xl" data-testid="edit-realtor-btn">
              <Pencil className="h-4 w-4 mr-2" /> Edit Realtor
            </Button>
          )}
        </div>

        {/* Profile Header */}
        <Card className="border-neutral-border shadow-card">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start gap-5">
              <ProfileAvatar marketer={marketer} size="lg" />
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-neutral-900">{marketer.full_name}</h2>
                <p className="text-neutral-500 mt-1">{marketer.email}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Badge className="bg-primary-100 text-primary-700">{marketer.recruits_count || 0} Recruits</Badge>
                  {marketer.referral_code && <Badge variant="secondary">Code: {marketer.referral_code}</Badge>}
                  {(marketer.recruits_count || 0) >= 50 && <Badge className="bg-green-100 text-green-800">Hit 50 Target</Badge>}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Personal Info */}
          <Card className="border-neutral-border shadow-card">
            <CardHeader><CardTitle className="text-lg">Personal Information</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              <InfoRow icon={Phone} label="WhatsApp" value={marketer.whatsapp} />
              <InfoRow icon={Mail} label="Email" value={marketer.email} />
              <InfoRow icon={MapPin} label="Location" value={marketer.location} />
              <InfoRow icon={User} label="Username" value={marketer.username} />
              <InfoRow icon={Link2} label="Referral Code" value={marketer.referral_code} />
              <InfoRow icon={Calendar} label="Joined" value={marketer.created_at ? new Date(marketer.created_at).toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' }) : null} />
            </CardContent>
          </Card>

          {/* Bank Details */}
          <Card className="border-neutral-border shadow-card">
            <CardHeader><CardTitle className="text-lg">Bank Details</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              <InfoRow icon={CreditCard} label="Bank Name" value={marketer.bank_name} />
              <InfoRow icon={CreditCard} label="Account Number" value={marketer.bank_account_number} />
              <InfoRow icon={CreditCard} label="Account Name" value={marketer.bank_account_name} />
            </CardContent>
          </Card>
        </div>

        {/* Upline */}
        {upline && (
          <Card className="border-neutral-border shadow-card">
            <CardHeader><CardTitle className="text-lg">Upline (Referred By)</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-neutral-50 cursor-pointer hover:bg-neutral-100 transition-colors" onClick={() => navigate(`/backoffice/realtors/${upline.id}`)}>
                <ProfileAvatar marketer={upline} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-primary-900">{upline.full_name}</p>
                  <p className="text-sm text-neutral-500">{upline.email}</p>
                </div>
                <Badge className="bg-primary-100 text-primary-700">{upline.recruits_count || 0} recruits</Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Downlines (Recruits) */}
        <Card className="border-neutral-border shadow-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-primary-600" /> Recruits ({downlines.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {downlines.length > 0 ? (
              <div className="space-y-2">
                {downlines.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 p-3 rounded-xl bg-neutral-50 cursor-pointer hover:bg-neutral-100 transition-colors" onClick={() => navigate(`/backoffice/realtors/${d.id}`)} data-testid={`recruit-${d.id}`}>
                    <ProfileAvatar marketer={d} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-neutral-900 truncate">{d.full_name}</p>
                      <p className="text-xs text-neutral-500">{d.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{d.recruits_count || 0} recruits</p>
                      <p className="text-xs text-neutral-400">{d.whatsapp}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-neutral-500 py-8">No recruits yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg" data-testid="edit-realtor-dialog">
          <DialogHeader><DialogTitle>Edit Realtor</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Full Name</Label><Input value={editForm.full_name || ''} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} data-testid="edit-realtor-name" /></div>
            <div><Label>WhatsApp</Label><Input value={editForm.whatsapp || ''} onChange={(e) => setEditForm({ ...editForm, whatsapp: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" value={editForm.email || ''} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Location</Label><Input value={editForm.location || ''} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Bank Name</Label><Input value={editForm.bank_name || ''} onChange={(e) => setEditForm({ ...editForm, bank_name: e.target.value })} /></div>
            <div><Label>Account Number</Label><Input value={editForm.bank_account_number || ''} onChange={(e) => setEditForm({ ...editForm, bank_account_number: e.target.value })} /></div>
            <div><Label>Account Name</Label><Input value={editForm.bank_account_name || ''} onChange={(e) => setEditForm({ ...editForm, bank_account_name: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Notes</Label><Input value={editForm.notes || ''} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={submitEdit} disabled={saving} data-testid="save-realtor-btn">{saving ? 'Saving...' : 'Save changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </BOLayout>
  );
};

export default BORealtorDetail;
